/**
 * テンプレート作成方法選択ダイアログ
 *
 * 「既存テンプレートから作成」または「完全新規作成」を選択
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Science as ScienceIcon
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border, shadow } from '../../../../utils/darkModeAdapter';
import { designTokens } from '../../../../theme/designTokens';
import { templateService } from '../../services/audioAPI';
import TemplateSelector from './TemplateSelector';

const theme = designTokens.featureThemes.interview_support;

/**
 * テンプレート作成方法選択ダイアログ
 */
export default function TemplateCreationDialog({
  open,
  onClose,
  onCreateFromTemplate,
  onCreateNew
}) {
  const muiTheme = useTheme();
  const [creationMode, setCreationMode] = useState('from_template'); // 'from_template' | 'new'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 作成開始
  const handleCreate = async () => {
    if (creationMode === 'from_template') {
      if (!selectedTemplate) {
        setError('ベースにするテンプレートを選択してください');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // テンプレートの詳細を取得
        const templateDetail = await templateService.getTemplate(selectedTemplate.id);
        onCreateFromTemplate(templateDetail);
        handleClose();
      } catch (err) {
        setError('テンプレートの取得に失敗しました');
        console.error('Failed to get template:', err);
      } finally {
        setIsLoading(false);
      }
    } else {
      onCreateNew();
      handleClose();
    }
  };

  // ダイアログを閉じる
  const handleClose = () => {
    setCreationMode('from_template');
    setSelectedTemplate(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: shadow(muiTheme).lg,
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          py: 2.5,
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.light} 100%)`,
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        テンプレートの作成方法を選択
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* 作成方法選択 */}
        <Grid container sx={{ minHeight: 200 }}>
          {/* 既存から作成 */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              borderRight: { md: `1px solid ${border(muiTheme).default}` },
              borderBottom: { xs: `1px solid ${border(muiTheme).default}`, md: 'none' }
            }}
          >
            <Box
              onClick={() => {
                setCreationMode('from_template');
                setError(null);
              }}
              sx={{
                p: 4,
                height: '100%',
                cursor: 'pointer',
                backgroundColor: creationMode === 'from_template' ? `${theme.primary}08` : 'transparent',

                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${theme.primary}05`
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: `${theme.primary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CopyIcon sx={{ color: theme.primary, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: textColors(muiTheme).primary }}>
                    既存テンプレートから作成
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    推奨
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
                科学的根拠に基づいたテンプレートをベースにカスタマイズできます。
                BARS評価基準や推奨質問が用意された状態から始められます。
              </Typography>
              <Alert
                severity="info"
                icon={<ScienceIcon fontSize="small" />}
                sx={{
                  py: 0.5,
                  fontSize: '0.75rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  '& .MuiAlert-message': { py: 0 }
                }}
              >
                構造化面接の予測妥当性 ρ=.42
              </Alert>
              {creationMode === 'from_template' && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="選択中"
                  size="small"
                  sx={{ mt: 1, backgroundColor: theme.primary, color: '#fff' }}
                />
              )}
            </Box>
          </Grid>

          {/* 完全新規作成 */}
          <Grid item xs={12} md={6}>
            <Box
              onClick={() => {
                setCreationMode('new');
                setSelectedTemplate(null);
                setError(null);
              }}
              sx={{
                p: 4,
                height: '100%',
                cursor: 'pointer',
                backgroundColor: creationMode === 'new' ? `${theme.primary}08` : 'transparent',

                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${theme.primary}05`
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: bg(muiTheme).tinted,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AddIcon sx={{ color: textColors(muiTheme).muted, fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: textColors(muiTheme).primary }}>
                  完全新規作成
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                空のフォームからすべて手動で設定します。
                コンピテンシー、BARS評価基準、質問をゼロから作成できます。
              </Typography>
              {creationMode === 'new' && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="選択中"
                  size="small"
                  sx={{ mt: 2, backgroundColor: theme.primary, color: '#fff' }}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* エラー表示 */}
        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* テンプレート選択エリア（既存から作成の場合） */}
        {creationMode === 'from_template' && (
          <Box sx={{ borderTop: `1px solid ${border(muiTheme).default}`, p: 3, backgroundColor: bg(muiTheme).subtle }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: textColors(muiTheme).secondary }}>
              ベースにするテンプレートを選択
            </Typography>
            <Box sx={{ maxHeight: '35vh', overflow: 'auto', backgroundColor: bg(muiTheme).card, borderRadius: 2, p: 2 }}>
              <TemplateSelector
                onSelectTemplate={setSelectedTemplate}
                selectedTemplateId={selectedTemplate?.id}
                showActions={false}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${border(muiTheme).default}`, backgroundColor: bg(muiTheme).subtle }}>
        <Button
          onClick={handleClose}
          sx={{ borderRadius: 2, color: textColors(muiTheme).muted }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={isLoading || (creationMode === 'from_template' && !selectedTemplate)}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.2,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }
          }}
        >
          {isLoading ? '読み込み中...' : '作成を開始'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
